import { getFunctions, httpsCallable } from 'firebase/functions';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { mixed, object, string } from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { FormProvider, RHFTextField, RHFUploadSingleFile } from '../../../components/hook-form';
// import { PATH_DASHBOARD } from '../../../routes/paths';
import content from './IntegrationGoogleWorkspace.md';
import Iconify from '...components/Iconify';
import Markdown from '...components/Markdown';
import { googleServiceAccountKeySchema } from '...data/common/schemas/serviceAccountKey';
import useAuth from '...hooks/useAuth';
import { app } from '...modules/firebase/web';

const functions = getFunctions(app);
const integrationsCreate = httpsCallable(functions, 'integrationsCreate');

const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1)
}));

const STEPS = ['Prepare', 'Create Integration', 'Verify'];

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  top: 10,
  left: 'calc(-50% + 20px)',
  right: 'calc(50% + 20px)',
  '& .MuiStepConnector-line': {
    borderTopWidth: 2,
    borderColor: theme.palette.divider
  },
  '&.Mui-active, &.Mui-completed': {
    '& .MuiStepConnector-line': {
      borderColor: theme.palette.primary.main
    }
  }
}));

function QontoStepIcon({ active, completed }) {
  return (
    <Box
      sx={{
        zIndex: 9,
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: active ? 'primary.main' : 'text.disabled'
      }}
    >
      {completed ? (
        <Iconify
          icon={'eva:checkmark-fill'}
          sx={{ zIndex: 1, width: 20, height: 20, color: 'primary.main' }}
        />
      ) : (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'currentColor'
          }}
        />
      )}
    </Box>
  );
}

QontoStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool
};

// ----------------------------------------------------------------------

export default function IntegrationGoogleWorkspace({ handleClose }) {
  // const navigate = useNavigate();
  const { account } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const GoogleWorkspaceIntegrationSchema = object({
    email: string().email().required('Email is required'),
    key: mixed()
      .test(
        'is-sak',
        'Key file is invalid. Please check that the file has `type`, `client_email`, `private_key`, and `private_key_id` fields.',
        (value) => {
          if (!value) return true;
          return googleServiceAccountKeySchema.isValid(value?.json);
        }
      )
      .required('Key is required')
  });

  const defaultValues = {
    email: '',
    key: null
  };

  const methods = useForm({
    resolver: yupResolver(GoogleWorkspaceIntegrationSchema),
    defaultValues
  });

  const {
    reset,
    // control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = methods;

  const verifyAndCreateIntegration = async ({ email, key }) => {
    setVerifying(true);
    try {
      await integrationsCreate({ accountId: account.id, type: 'GoogleFetch', email, key });
      setVerifying(false);
      setVerified(true);
      enqueueSnackbar('Integration created.');
    } catch (e) {
      setVerifying(false);
      setVerified(false);
    }
  };

  const onSubmit = async ({ email, key }) => {
    // SECURITY (Aug 7): never log `key` in full — it's the raw uploaded
    // service-account JSON, including the private key. This ran in the
    // browser console on every integration setup submission.
    console.log('ON SUBMIT VALUES', email, key?.json?.client_email);
    const { json } = key;
    try {
      // put json in as integration
      await new Promise((resolve) => setTimeout(resolve, 500));

      setActiveStep(2);
      verifyAndCreateIntegration({ email, key: json });
    } catch (error) {
      console.error(error);
    }
  };

  const processFile = useCallback(
    (file) => {
      const reader = new FileReader();
      reader.onload = function (e) {
        const json = JSON.parse(e.target.result);
        setValue('key', Object.assign(file, { json }));
      };
      reader.readAsText(file);
    },
    [setValue]
  );

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      processFile(file);
    }
  };

  const onFinished = () => {
    reset();
    handleClose();
  };

  return (
    <>
      <Grid item sx={{ width: 500, mt: 4 }}>
        <Stepper alternativeLabel activeStep={activeStep} connector={<QontoConnector />}>
          {STEPS.map((label, i) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={QontoStepIcon}
                sx={{
                  cursor: i < activeStep ? 'pointer' : 'initial',
                  '& .MuiStepLabel-label': {
                    typography: 'subtitle2',
                    color: 'text.disabled'
                  }
                }}
                onClick={() => i < activeStep && setActiveStep(i)}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Grid>

      {activeStep === 0 && (
        <Grid container item justifyContent="center" alignItems="flex-start" sx={{ mt: -2 }}>
          <Grid item xs={12} md={8}>
            <Markdown>{content}</Markdown>
            <div>
              <Box sx={{ mt: 8, mb: 8 }}>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  onClick={() => setActiveStep(1)}
                >
                  Continue
                </LoadingButton>
              </Box>
            </div>
          </Grid>
        </Grid>
      )}

      {activeStep === 1 && (
        <Grid container item justifyContent="center" alignItems="flex-start" sx={{ mt: 4 }}>
          <Grid item xs={12} md={8} sx={{ mb: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Enter data from the previous step
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={8} sx={{ mb: 2 }}>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <div>
                    <LabelStyle>Enter your email on the Google Workspace domain</LabelStyle>
                    <RHFTextField name="email" label="Super Administrator's Email" />
                  </div>
                  <div>
                    <LabelStyle>Upload the Service Account Key</LabelStyle>
                    <RHFUploadSingleFile
                      name="key"
                      mimeType="application/JSON"
                      maxSize={10000}
                      onDrop={handleDrop}
                    />
                  </div>
                </Stack>
              </Card>
              <Stack direction="row" spacing={1.5} sx={{ mt: 8, mb: 8 }}>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ px: 4 }}
                  startIcon={<Iconify icon={'eva:arrow-ios-back-fill'} />}
                  onClick={() => setActiveStep(0)}
                >
                  Prepare
                </Button>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={isSubmitting}
                >
                  Continue
                </LoadingButton>
              </Stack>
            </FormProvider>
          </Grid>
        </Grid>
      )}

      {activeStep === 2 && (
        <Grid container item justifyContent="center" alignItems="flex-start" sx={{ mt: 4 }}>
          <Grid item xs={12} md={8} sx={{ mb: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Verifying the integration.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={8} sx={{ mb: 2 }}>
            <Card sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box>Checking access...</Box>
                {!verifying && !verified && (
                  <Box>
                    Verification failed. Please go back, review the instructions and try again.
                  </Box>
                )}
                {verified && <Box>Integration successful!</Box>}
              </Stack>
            </Card>
            <Stack direction="row" spacing={1.5} sx={{ mt: 8, mb: 8 }}>
              {!verifying && !verified && (
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ px: 4 }}
                  startIcon={<Iconify icon={'eva:arrow-ios-back-fill'} />}
                  onClick={() => setActiveStep(0)}
                >
                  Back
                </Button>
              )}
              <LoadingButton
                fullWidth
                onClick={onFinished}
                variant="contained"
                size="large"
                disabled={verifying || !verified}
                loading={verifying}
              >
                Finished
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      )}
    </>
  );
}
